import { MultiPolygon, Point, Polygon } from "@turf/helpers";
import { DataFilterExtension } from "@deck.gl/extensions";
import { SITES_CLUSTERING_CONFIG, SitesFilters } from "@landtechnologies/search-layer-contracts";
import { ENVIRONMENT } from "src/js/util/environment";
import { showError } from "src/js/stores/user/actions/addNotification";
import { SiteMVTFeature } from "react-migration/layouts/map/Multilayer/layer_types/SitesLayerType/utils";
import {
  ClusteredViewportSearchMapLayer,
  ClusteredViewportSearchMapLayerProps,
  ParsedClusterPoint,
} from "react-migration/lib/map/layers/ClusteredViewportSearchMapLayer";
import { SitesMVTLayer } from "../SitesMVTLayer";
import { HIDDEN, VISIBLE } from "../constants";
import SITES_CLUSTER_ICON_MAPPING from "./sites-cluster.mapping.json";
import SITES_CLUSTER_ICON from "./sites-cluster@2x.png";

type ClusteredSiteDocument = {
  geometry: Polygon | MultiPolygon | Point;
};

// eslint-disable-next-line @typescript-eslint/ban-types
type ClusteredSiteFeatureProps = {};

// eslint-disable-next-line @typescript-eslint/ban-types
type SitesClusteredAggregations = {
  stage: Record<string, number>;
};

type SitesClusteredViewportSearchMapLayerProps = Omit<
  ClusteredViewportSearchMapLayerProps<
    ClusteredSiteDocument,
    ClusteredSiteFeatureProps,
    SitesClusteredAggregations,
    SitesFilters
  >,
  | "FeatureLayerClass"
  | "getClusterFillColor"
  | "getClusterRadius"
  | "onFetchClusterError"
  | "renderFeatureLayers"
  | "url"
> & {
  stageLookup: Record<string, { color: string }>;
  sitesDirtyCount: number;
};

export type SitesCluster = ParsedClusterPoint<ClusteredSiteDocument, SitesClusteredAggregations>;

export class SitesClusteredViewportSearchMapLayer extends ClusteredViewportSearchMapLayer<
  ClusteredSiteDocument,
  ClusteredSiteFeatureProps,
  SitesClusteredAggregations
> {
  static layerName = "SitesClusteredViewportSearchMapLayer";

  constructor(props: SitesClusteredViewportSearchMapLayerProps) {
    super({
      ...props,
      url: `${ENVIRONMENT.SUPPORTING_SERVICE_URL}/sites-layer/clustered-viewport`,
      dataDeps: [props.sitesDirtyCount],
      clusteringConfig: SITES_CLUSTERING_CONFIG,
      getClusterTextSize: 12,

      getClusterRadius: 20,
      getClusterIcon: () => "rounded-square",
      clusterIconAtlas: SITES_CLUSTER_ICON,
      clusterIconMapping: SITES_CLUSTER_ICON_MAPPING,

      onClusterHover: props.onClusterHover,
      renderFeatureLayers: (features) => {
        const { stageLookup: stageColourLookup, sitesDirtyCount, filters } = props;
        const { showSharedSites, workspaces } = filters;
        const workspaceId = workspaces?.[0];
        const visibleFeatureIds = new Set(features.map((feature) => feature.id));
        const { minReturnResultsPrecision } = SITES_CLUSTERING_CONFIG;
        const deckGlZoomOffset = -1;
        const minZoom = minReturnResultsPrecision + deckGlZoomOffset;

        if (!workspaceId) {
          return [];
        }

        return [
          new SitesMVTLayer(
            this.getSubLayerProps({
              minZoom,
              stageLookup: stageColourLookup,
              workspaceId,
              updateTriggers: {
                getLineColor: [stageColourLookup, sitesDirtyCount],
                getTileData: [sitesDirtyCount],
                getFilterValue: visibleFeatureIds,
              },
              extensions: [new DataFilterExtension({ filterSize: 1 })],
              getFilterValue: (d: SiteMVTFeature) => {
                if (d.properties.shared) {
                  return showSharedSites ? VISIBLE : HIDDEN;
                }

                return visibleFeatureIds.has(d.properties.id) ? VISIBLE : HIDDEN;
              },
              filterRange: [0, 1],
            })
          ),
        ];
      },
      onFetchClusterError: (error) => {
        console.error(error);

        showError({
          id: `clustered-viewport-layer_${props.id}_search-error`,
          title: "Error searching sites",
          description: "Failed to load sites layer",
          duration: 10_000,
        });
      },
    });
  }
}
